import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend } from './api';

export type Role = 'USER' | 'ADMIN';

/** Payload of `GET /api/me`. `avatarUrl` is null when the Discord account has no custom avatar. */
export interface Me {
  id: number;
  username: string;
  avatarUrl: string | null;
  role: Role;
}

export const ME_QUERY_KEY = ['me'] as const;

export const meQuery = {
  queryKey: ME_QUERY_KEY,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<Me>('/api/me', signal),
  // A 401 is a legitimate answer, not a transient failure — retrying it just delays the redirect.
  retry: false,
  staleTime: 60_000,
};

export function useMe() {
  return useQuery(meQuery);
}

/**
 * `POST /api/logout` invalidates the server-side session; clearing the query cache drops every
 * cached answer that was scoped to that user.
 */
export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiSend<void>('POST', '/api/logout'),
    onSettled: () => {
      queryClient.clear();
    },
  });
}

/** Full-page navigation: the OAuth dance leaves the SPA and comes back through Caddy. */
export const DISCORD_LOGIN_URL = '/oauth2/authorization/discord';
